<template>
  <popup-dialog
    :visible="visible"
    :title="title"
    :close-button-visible="true"
    :width="900"
    class="overlay-influence-matrix"
  >
    <article class="overlay-influence-matrix__content">
      <input-state-toggle
        :states="['impact', 'flow']"
        :value="ctx.impactMatrix.relationType"
        v-slot="{ state }"
        @update:value="ctx.updateKey('relationType', $event)"
      >
        {{ $t(`objectNames.${state}`, 5) }}
      </input-state-toggle>

      <section class="overlay-influence-matrix__main">
        <cross-impact-matrix
          class="overlay-influence-matrix__matrix"
          :matrix-id="matrixId"
        />
        <influence-list :matrix-id="matrixId" />
      </section>

      <hr class="mb-0" />

      <details-block class="mt-4 overlay-influence-matrix__elements">
        <template #header>{{ $t("objectNames.element", 4) }}</template>
        <div>
          <h3 class="label">{{ $t("objectNames.target", 5) }}</h3>
          <div class="line--wrap">
            <button
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.impactMatrix.allTargets }"
              @click="ctx.updateKey('allTargets', !ctx.impactMatrix.allTargets)"
            >
              {{ $t("labels.allTargets") }}
            </button>
            <button
              v-for="item of targets"
              :key="item.uid"
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.elementUids.has(item.uid), '-all-selected': ctx.impactMatrix.allTargets }"
              @click="toggleItem(item.uid)"
            >
              {{ item.title }}
            </button>
          </div>

          <h3 class="label">{{ $t("objectNames.resource", 5) }}</h3>
          <div class="line--wrap">
            <button
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.impactMatrix.allResources }"
              @click="
                ctx.updateKey('allResources', !ctx.impactMatrix.allResources)
              "
            >
              {{ $t("labels.allResources") }}
            </button>
            <button
              v-for="item of resources"
              :key="item.uid"
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.elementUids.has(item.uid), '-all-selected': ctx.impactMatrix.allResources }"
              @click="toggleItem(item.uid)"
            >
              {{ item.title }}
            </button>
          </div>

          <h3 class="label">{{ $t("objectNames.index", 5) }}</h3>
          <div class="line--wrap">
            <button
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.impactMatrix.allIndices }"
              @click="ctx.updateKey('allIndices', !ctx.impactMatrix.allIndices)"
            >
              {{ $t("labels.allIndices") }}
            </button>
            <button
              v-for="item of indices"
              :key="item.uid"
              type="button"
              class="button-pill"
              :class="{ '-selected': ctx.elementUids.has(item.uid), '-all-selected': ctx.impactMatrix.allIndices }"
              @click="toggleItem(item.uid)"
            >
              {{ item.title }}
            </button>
          </div>
        </div>
      </details-block>
    </article>
  </popup-dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";

import PopupDialog from "@core/ui/PopupDialog.vue";
import CrossImpactMatrix from "@editor/items/impactMatrix/CrossImpactMatrix.vue";
import InfluenceList from "@editor/items/impactMatrix/InfluenceList.vue";
import DetailsBlock from "@core/ui/DetailsBlock.vue";
import InputStateToggle from "@core/input/InputStateToggle.vue";

import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useImpactMatrixStore } from "@editor/items/impactMatrix/impactMatrices.store";
import { useImpactMatrix } from "@editor/items/impactMatrix/useImpactMatrix";
import { splitUid } from "@/helpers/utility/Id";

const props = defineProps<{
  visible: boolean;
  matrixId: number;
}>();

const ctx = useImpactMatrix(() => props.matrixId);

const matrixStore = useImpactMatrixStore();

const targets = computed(() => useTargetStore().orderedItems);
const resources = computed(() => useResourceStore().orderedItems);
const indices = computed(() => useIndexStore().orderedItems);

const title = computed(() => {
  return ctx.matrixTypeTitle + ' – ' + ctx.title;
});

function toggleItem<T extends "target" | "resource" | "index">(elementUid: `${T}_${number}`) {
  if (props.matrixId == null) return;

  const { type } = splitUid(elementUid);

  const matrix = matrixStore.items[props.matrixId];

  if (type == "target" && matrix.allTargets) {
    matrixStore.update({
      id: props.matrixId,
      allTargets: false,
      elementUids: [elementUid]
    });
    return;
  }
  if (type == "resource" && matrix.allResources) {
    matrixStore.update({
      id: props.matrixId,
      allResources: false,
      elementUids: [elementUid]
    });
    return;
  }
  if (type == "index" && matrix.allIndices) {
    matrixStore.update({
      id: props.matrixId,
      allIndices: false,
      elementUids: [elementUid]
    });
    return;
  }

  const elementUids = new Set(
    matrix.elementUids ?? []
  );

  // add or remove uid
  if (elementUids.has(elementUid)) {
    elementUids.delete(elementUid);
  } else {
    elementUids.add(elementUid);
  }

  matrixStore.update({
    id: props.matrixId,
    elementUids: Array.from(elementUids)
  });
}
</script>

<style lang="scss">
.overlay-influence-matrix {
  &__content {
    padding: 2rem;
    gap: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  &__main {
    display: grid;
    align-items: flex-start;
    grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
    gap: 1.5rem;
  }

  &__matrix {
    position: sticky;
    top: 0;
  }

  .button-pill.-all-selected {
    opacity: 0.7;
  }
}
</style>
